import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, AlertCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AuthConfirm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);

  const sendAdminNotification = async (user: any) => {
    try {
      // Get user profile data
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      await supabase.functions.invoke('send-admin-notification', {
        body: {
          userEmail: user.email,
          firstName: profile?.first_name,
          lastName: profile?.last_name,
          profileType: profile?.profile_type,
          profession: profile?.profession,
          location: profile?.location,
          education: profile?.education,
          signupTimestamp: user.created_at
        }
      });
    } catch (error) {
      console.error('Failed to send admin notification:', error);
      // Don't block the user experience if notification fails
    }
  };

  const handleResendVerification = async () => {
    if (!resendEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive"
      });
      return;
    }

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: resendEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`
        }
      });

      if (error) {
        console.error('Resend error:', error);
        toast({
          title: "Error resending email",
          description: error.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Verification email sent",
          description: "Please check your email for a new verification link",
        });
        setShowResendForm(false);
        setResendEmail('');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      toast({
        title: "Error",
        description: "Failed to resend verification email. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Log all URL parameters for debugging
        const allParams = Object.fromEntries(searchParams.entries());
        console.log('Auth confirmation URL parameters:', allParams);

        // Check for error parameters first
        const error = searchParams.get('error');
        const errorCode = searchParams.get('error_code');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          console.log('Error in URL:', { error, errorCode, errorDescription });
          setVerificationStatus('error');
          
          if (error === 'access_denied' && errorCode === 'otp_expired') {
            setErrorMessage("Your verification link has expired. You can request a new verification email below.");
          } else if (error === 'access_denied') {
            setErrorMessage("Access denied. The verification link may be invalid or expired.");
          } else {
            setErrorMessage(errorDescription || "There was an error with email verification. Please try again.");
          }
          setIsLoading(false);
          return;
        }

        // Check for success parameters
        const token_hash = searchParams.get('token_hash');
        const type = searchParams.get('type');

        if (token_hash && type) {
          console.log('Attempting email verification with token_hash and type:', type);
          
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash,
            type: type as any,
          });

          if (verifyError) {
            console.error('Email confirmation error:', verifyError);
            setVerificationStatus('error');
            
            if (verifyError.message?.includes('expired') || verifyError.message?.includes('invalid')) {
              setErrorMessage("Your verification link has expired or is invalid. You can request a new verification email below.");
            } else {
              setErrorMessage("There was an error verifying your email. Please try again.");
            }
          } else {
            console.log('Email verification successful');
            setVerificationStatus('success');
            
            // Get current user to send admin notification
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              console.log('Sending admin notification for user:', user.email);
              await sendAdminNotification(user);
            }
          }
        } else {
          console.log('Missing required parameters: token_hash or type');
          setVerificationStatus('error');
          setErrorMessage("The verification link is invalid or incomplete.");
        }
      } catch (error) {
        console.error('Confirmation error:', error);
        setVerificationStatus('error');
        setErrorMessage("There was an error verifying your email. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    handleEmailConfirmation();
  }, [searchParams]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-muted-foreground">Verifying your email...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="mx-auto max-w-md text-center space-y-6 p-8">
        {verificationStatus === 'success' ? (
          <>
            <div className="flex justify-center">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-foreground">Email Verified Successfully!</h1>
              <p className="text-muted-foreground">
                Your email has been verified and your account is now pending administrator approval.
              </p>
              <p className="text-sm text-muted-foreground">
                You will receive an email notification once your account is approved and you can log in.
              </p>
            </div>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Continue to Login
            </Button>
          </>
        ) : (
          <>
            <div className="flex justify-center">
              <AlertCircle className="h-16 w-16 text-destructive" />
            </div>
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-foreground">Verification Failed</h1>
              <p className="text-muted-foreground">{errorMessage}</p>
            </div>
            
            {/* Show resend form if link is expired */}
            {(errorMessage.includes('expired') || errorMessage.includes('invalid')) && !showResendForm && (
              <div className="space-y-3">
                <Button 
                  onClick={() => setShowResendForm(true)}
                  variant="outline"
                  className="w-full"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Request New Verification Email
                </Button>
              </div>
            )}

            {/* Resend verification form */}
            {showResendForm && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                <div className="space-y-2">
                  <Label htmlFor="resend-email">Email Address</Label>
                  <Input
                    id="resend-email"
                    type="email"
                    placeholder="Enter your email address"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    disabled={isResending}
                  />
                </div>
                <div className="flex space-x-2">
                  <Button 
                    onClick={handleResendVerification}
                    disabled={isResending || !resendEmail}
                    className="flex-1"
                  >
                    {isResending ? (
                      <>
                        <LoadingSpinner />
                        <span className="ml-2">Sending...</span>
                      </>
                    ) : (
                      'Send Verification Email'
                    )}
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowResendForm(false);
                      setResendEmail('');
                    }}
                    variant="outline"
                    disabled={isResending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <Button 
              onClick={() => navigate('/signup')}
              className="w-full"
              variant="outline"
            >
              Return to Sign Up
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthConfirm;